import React, { useEffect, useState } from "react";
import {
  Table,
  Pagination,
  PaginationProps,
  TableColumnType,
  TablePaginationConfig,
  TableProps,
} from "antd";
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
} from "antd/es/table/interface";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { FormInstance } from "antd/lib";
import "./table-data.scss";

type TableDataProps = {
  dataTable?: any[];
  dataColumns?: TableColumnType<any>[];
  totalRecords?: number;
  pageNumber?: number;
  pageSize?: number;
  scroll?: { x: number; y: number };
  loadingTable?: boolean;
  hideSelection?: boolean;
  selectOneRow?: boolean;
  rowSelected?: any[];
  clearRowkey?: boolean;
  pagination?: boolean;
  setPageNumber?: (p: number) => void;
  setPageSize?: (s: number) => void;
  setSelectedRowKeysProps?: (rows: any[]) => void;
  handleTableChangeProps: (
    p: TablePaginationConfig,
    f: Record<string, FilterValue | null>,
    s: SorterResult<any> | SorterResult<any>[],
    e: TableCurrentDataSource<any>
  ) => void;

  checkEdit?: boolean;
  unit?: string;
  localeProp?: any;
  form?: FormInstance;
  searchValue?: string | null;
  disableRowCheckbox?: (record: any) => boolean;
  hideDisabledCheckbox?: boolean;
  selectionColumnWidth?: number;
};

const TableData: React.FC<TableDataProps> = ({
  dataTable,
  dataColumns,
  totalRecords,
  pageNumber,
  pageSize,
  pagination,
  setPageNumber,
  setPageSize,
  hideSelection = false,
  selectOneRow = false,
  rowSelected,
  clearRowkey,
  setSelectedRowKeysProps,
  loadingTable,
  scroll,
  handleTableChangeProps,
  unit,
  localeProp,
  form,
  searchValue,
  disableRowCheckbox,
  hideDisabledCheckbox,
  selectionColumnWidth,
}) => {
  const { t } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [totalPage, setTotalPage] = useState(totalRecords);

  useEffect(() => setTotalPage(totalRecords), [totalRecords]);

  useEffect(() => {
    if (clearRowkey) setSelectedRowKeys([]);
  }, [clearRowkey]);

  useEffect(() => {
    if (rowSelected) {
      if (rowSelected.length === 0) {
        setSelectedRowKeys([]);
      } else {
        setSelectedRowKeys(rowSelected.map((r) => r.id));
      }
    }
  }, [rowSelected]);

  useEffect(() => {
    if (pagination && pageNumber && pageSize && totalRecords !== undefined) {
      const maxPage = Math.ceil(totalRecords / pageSize) || 1;

      if (pageNumber > maxPage && (!dataTable || dataTable.length === 0)) {
        setPageNumber?.(maxPage);
      }
    }
  }, [dataTable, totalRecords, pageNumber, pageSize, pagination, setPageNumber]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[], rows: any[]) => {
      if (selectOneRow) {
        if (
          selectedRowKeys.length === 1 &&
          keys.length === 1 &&
          selectedRowKeys[0] === keys[0]
        ) {
          keys = [];
          rows = [];
        } else if (keys.length > 1) {
          keys = [keys.at(-1)!];
          rows = [rows.at(-1)!];
        }
      }
      setSelectedRowKeys(keys);
      setSelectedRowKeysProps?.(rows);
      form?.setFieldsValue({ nguoiDungs: keys });
    },
    preserveSelectedRowKeys: true,
    getCheckboxProps: (record: any) => ({
      disabled: disableRowCheckbox?.(record) ?? false,
    }),

    renderCell: (
      _checked: boolean,
      record: any,
      _index: number,
      originNode: React.ReactNode
    ) =>
      disableRowCheckbox?.(record) && hideDisabledCheckbox
        ? null
        : originNode,
  };

  const handleTableChange: TableProps["onChange"] = (
    p,
    f,
    s,
    extra
  ) => handleTableChangeProps(p, f, s, extra);

  const _data = dataTable?.map((r, idx) => ({
    ...r,
    numericalOrder:
      pagination && pageSize && pageNumber
        ? pageSize * (pageNumber - 1) + idx + 1
        : idx + 1,
  }));

  const translatedColumns = dataColumns?.map((col: any) => ({
    ...col,
    title: typeof col.title === "string" ? t(col.title, col.title) : col.title,
  }));

  const localeMerged =
    localeProp || {
      emptyText: (
        <span className="empty-table">
          <p>
            <img
              className="image-empty-data"
              src="/images/empty-img-gray.png"
              alt="empty"
            />
          </p>
          <p className="nodata-text">
            {searchValue
              ? `${t("Không tìm thấy kết quả với từ khóa", "Không tìm thấy kết quả với từ khóa")} "${searchValue}"`
              : t("Không tìm thấy", "Không tìm thấy")}
          </p>
        </span>
      ),
      triggerDesc: t("Sắp xếp theo thứ tự Z-A", "Sắp xếp theo thứ tự Z-A"),
      triggerAsc: t("Sắp xếp thứ tự A-Z", "Sắp xếp thứ tự A-Z"),
      cancelSort: t("Huỷ sắp xếp", "Huỷ sắp xếp"),
    };

  return (
    <div className="table-wrapper">
      <Table
        size="middle"
        tableLayout="fixed"
        rowSelection={
          hideSelection
            ? {
              type: "checkbox",
              columnWidth: selectionColumnWidth ? selectionColumnWidth : 48,
              fixed: "left",
              ...rowSelection,
            }
            : undefined
        }
        columns={translatedColumns}
        dataSource={_data}
        loading={loadingTable}
        locale={localeMerged}
        pagination={false}
        scroll={scroll}
        rowKey={(r) => r.id}
        onChange={handleTableChange}
      />

      {pagination && (
        <Pagination
          total={totalPage}
          showSizeChanger
          pageSize={pageSize}
          current={pageNumber}
          className="pagination-table"
          onChange={(p, s) => {
            setPageNumber?.(p);
            setPageSize?.(s);
            setTotalPage(totalRecords);
          }}
          itemRender={(_, type, original) =>
            type === "prev" ? <LeftOutlined /> :
              type === "next" ? <RightOutlined /> : original
          }
          locale={{
            items_per_page: t("/ trang", "/ trang"),
            jump_to: t("Đi đến trang", "Đi đến trang"),
            page: "",
          }}
          showQuickJumper
          showTotal={(total) => {
            const unitLabel = unit ? t(unit, unit) : t("bản ghi", "bản ghi");
            return selectedRowKeys.length
              ? `${t("Đã chọn", "Đã chọn")} ${selectedRowKeys.length}/${total} ${unitLabel}`
              : `${t("Tổng số", "Tổng số")} ${total} ${unitLabel}`;
          }}
          pageSizeOptions={["10", "20", "50", "100"]}
        />
      )}
    </div>
  );
};

export default TableData;
